/**
 * The application's outermost error boundary.
 *
 * `index.tsx` rendered <App/> bare, so any throw during render unmounted the
 * whole tree and left a white screen — no message, no way back. React only
 * offers this seam to class components, which is why this one file departs
 * from the functional-components rule.
 *
 * The narrower boundary inside AssistantMessage stays where it is: it catches
 * a single bad message without taking the chat down, and only what escapes it
 * reaches here.
 */

import React from "react";

interface AppErrorBoundaryProps {
	children: React.ReactNode;
}

interface AppErrorBoundaryState {
	hasError: boolean;
}

class AppErrorBoundary extends React.Component<
	AppErrorBoundaryProps,
	AppErrorBoundaryState
> {
	state: AppErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): AppErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo): void {
		// The details belong in the console for whoever is debugging — never on
		// screen, where a stack trace reads as a crash the user caused.
		console.error("[AppErrorBoundary] Unhandled render error:", error, info);
	}

	private handleReload = (): void => {
		window.location.reload();
	};

	render(): React.ReactNode {
		if (!this.state.hasError) return this.props.children;

		return (
			<div
				role="alert"
				className="flex h-screen w-full items-center justify-center bg-gray-50 px-6"
			>
				<div className="max-w-md text-center">
					<h1 className="text-xl font-semibold text-text-strong">
						Something went wrong
					</h1>
					{/* No reassurance about saved work: a draft message, a file
					    selection and an unsent form live in this tab only, and the
					    reload below is what ends them. */}
					<p className="mt-2 text-base text-text-muted">
						The page ran into an unexpected problem. Reloading usually fixes
						it, but anything you had not sent yet will be lost.
					</p>
					<button
						type="button"
						onClick={this.handleReload}
						className="mt-6 rounded-lg bg-[#008782] px-4 py-2 text-sm font-medium text-white hover:bg-[#004C4B]"
					>
						Reload
					</button>
				</div>
			</div>
		);
	}
}

export default AppErrorBoundary;
